import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FolderOpen, FileText, Receipt, CheckCircle2, Mic, Plus, Euro, CalendarDays,
  ClipboardCheck, AlertTriangle, TrendingUp, ArrowRight, Sparkles, Clock, Wallet, Building2, CalendarClock, Briefcase,
} from "lucide-react";
import { StudioLayout } from "./StudioLayout";
import { AlertsPanel } from "@/components/studio/AlertsPanel";
import { QuickActions, InvoicesWidget, QuotesWidget } from "@/components/studio/CockpitWidgets";
import { ContractsWidget } from "@/components/studio/ContractsWidget";
import { FiscalCalendar } from "@/components/studio/FiscalCalendar";
import { studio, eur, beDate } from "@/lib/studioApi";
import { Card, SectionCard, MetricCard, ListRow, EmptyState, Skeleton } from "@/components/studio/StudioUI";

const TONE_RING = {
  blue: "border-[#C9D4F4] bg-[#EEF1FC]/70 text-[#2F4CA8]",
  amber: "border-[#E2E7F7] bg-[#F6F8FD] text-[#0F2645]",
  rose: "border-[#B42318]/20 bg-[#B42318]/[0.06] text-[#B42318]",
};

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Bonjour";
  if (h < 18) return "Bon après-midi";
  return "Bonsoir";
};

const longDate = () =>
  new Date().toLocaleDateString("fr-BE", { weekday: "long", day: "numeric", month: "long" });

export default function StudioHome() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const name = (localStorage.getItem("devisto_name") || "").split(" ")[0];

  useEffect(() => {
    studio
      .get("/dashboard")
      .then(({ data: d }) => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  const owner = data?.is_owner !== false;
  const inv = data?.invoices;
  const agendaCount = data?.agenda?.length ?? 0;
  const heroParts = loading
    ? ["Chargement de votre activité…"]
    : [
        agendaCount ? `${agendaCount} rendez-vous sur 7 jours` : "rien de planifié cette semaine",
        data?.quotes_sent ? `${data.quotes_sent} devis en attente de réponse` : null,
        owner && inv?.late_count ? `${inv.late_count} facture${inv.late_count > 1 ? "s" : ""} en retard` : null,
      ].filter(Boolean);

  return (
    <StudioLayout
      title="Tableau de bord"
      subtitle={`${longDate().charAt(0).toUpperCase()}${longDate().slice(1)}`}
      action={
        <Link
          to="/studio/dossiers?nouveau=1"
          data-testid="cockpit-new-dossier"
          className="hidden min-h-[42px] items-center gap-2 rounded-xl bg-[#3A5CC8] shadow-[0_1px_2px_rgba(15,38,69,0.14)] px-5 text-sm font-semibold text-white transition-[background-color,transform] duration-150 hover:bg-[#2F4CA8] active:scale-[0.98] lg:inline-flex"
        >
          <Plus className="h-4 w-4" /> Nouveau dossier
        </Link>
      }
    >
      <QuickActions name={name} />

      {/* ---------- Bandeau cockpit ---------- */}
      <section
        data-testid="cockpit-hero"
        className="dv-card-in relative mt-5 overflow-hidden rounded-2xl bg-[#0F2645] p-5 text-white sm:p-7"
      >
        <div className="relative grid grid-cols-1 gap-6 lg:grid-cols-[1.3fr_1fr] lg:items-end">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#9FB4F2]">{greeting()}{name ? `, ${name}` : ""}</p>
            <h2 data-testid="cockpit-hero-line" className="mt-2 flex flex-wrap items-baseline gap-x-2 font-heading text-xl font-bold leading-snug tracking-tight sm:text-2xl">
              {heroParts.map((part, i) => (
                <span key={part} className="flex items-baseline gap-x-2 sm:inline-flex sm:whitespace-nowrap">
                  {i > 0 && <span className="hidden text-[#F97316] sm:inline">·</span>}
                  <span className="whitespace-normal">{i === 0 ? part.charAt(0).toUpperCase() + part.slice(1) : part}{i === heroParts.length - 1 ? "." : ""}</span>
                </span>
              ))}
            </h2>
            <div className="mt-5 flex flex-wrap gap-2">
              <Link
                to="/studio/devis"
                data-testid="cockpit-hero-dictate"
                className="inline-flex h-11 items-center gap-2 rounded-xl bg-white px-5 text-sm font-semibold text-[#0F2645] transition-[background-color,transform] duration-150 hover:bg-[#EEF1FC] active:scale-[0.98]"
              >
                <Mic className="h-4 w-4 text-[#3A5CC8]" /> Dicter un devis
              </Link>
              <Link
                to="/studio/interventions?nouveau=1"
                data-testid="cockpit-hero-intervention"
                className="inline-flex h-11 items-center gap-2 rounded-xl border border-white/20 px-5 text-sm font-semibold text-white transition-colors hover:bg-white/10"
              >
                <ClipboardCheck className="h-4 w-4" /> Intervention
              </Link>
            </div>
          </div>
          {owner && (
            <div className="grid grid-cols-2 divide-x divide-white/10 rounded-xl border border-white/10 bg-white/[0.05]">
              <div className="p-3.5">
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#9FB4F2]">Signé ce mois</p>
                <p data-testid="cockpit-hero-month" className="mt-1 font-mono text-xl font-bold tabular-nums sm:text-2xl">{loading ? "—" : eur(data?.month_accepted)}</p>
              </div>
              <div className="p-3.5">
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#9FB4F2]">À encaisser</p>
                <p data-testid="cockpit-hero-open" className="mt-1 font-mono text-xl font-bold tabular-nums sm:text-2xl">{loading ? "—" : eur(inv?.open_amount)}</p>
              </div>
            </div>
          )}
        </div>
      </section>

      {owner && !loading && <AlertsPanel />}
      {owner && !loading && data && !data.accountant_auto_export && (
        <section data-testid="accountant-reminder-card" className="dv-card-in mt-4 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-[#C9D4F4] bg-[#EEF1FC] p-4 sm:p-5">
          <div className="flex min-w-0 items-start gap-3">
            <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-[#3A5CC8] shadow-[0_1px_2px_rgba(15,38,69,0.08)]"><Briefcase className="h-5 w-5" /></span>
            <div className="min-w-0">
              <p className="font-heading text-[15px] font-bold text-[#0F2645]">Votre comptable n'a pas encore son envoi trimestriel automatique</p>
              <p className="mt-0.5 text-sm leading-relaxed text-[#5C6A87]">
                {data.accountant_email
                  ? `L'e-mail ${data.accountant_email} est renseigné : activez l'envoi pour que l'export (ventes, achats, règlements, UBL) parte seul chaque 1er janvier, avril, juillet et octobre.`
                  : "Renseignez l'e-mail de votre comptable et activez l'envoi : l'export du trimestre (ventes, achats, règlements, UBL) partira seul, plus rien à préparer pour la TVA."}
              </p>
            </div>
          </div>
          <Link to="/studio/mon-comptable" data-testid="accountant-reminder-cta" className="inline-flex min-h-[42px] shrink-0 items-center gap-2 rounded-xl bg-[#3A5CC8] px-4 text-sm font-semibold text-white transition-colors hover:bg-[#2F4CA8]">
            Ouvrir « Mon comptable » <ArrowRight className="h-4 w-4" />
          </Link>
        </section>
      )}
      {owner && !loading && <ContractsWidget />}
      {owner && !loading && <FiscalCalendar />}

      {/* ---------- Ce qui compte aujourd'hui ---------- */}
      {loading ? (
        <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-28" />)}
        </div>
      ) : (
        <div data-testid="cockpit-kpis" className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <MetricCard
            testId="metric-card-agenda"
            delay={0}
            label="Agenda 7 jours"
            value={data?.agenda?.length ?? 0}
            hint="Visites et interventions"
            icon={CalendarDays}
            tone="blue"
            to="/studio/planning"
          />
          <MetricCard
            testId="metric-card-pending"
            delay={60}
            label="Devis sans réponse"
            value={data?.quotes_sent ?? 0}
            hint={owner ? eur(data?.pending_amount) : "Montants masqués"}
            icon={Clock}
            tone={data?.quotes_sent ? "amber" : "slate"}
            to="/studio/devis"
          />
          {owner ? (
            <MetricCard
              testId="metric-card-late"
            delay={120}
              label="Factures en retard"
              value={inv?.late_count ?? 0}
              hint={eur(inv?.late_amount)}
              icon={AlertTriangle}
              tone={inv?.late_count ? "rose" : "slate"}
              to="/studio/factures"
            />
          ) : (
            <MetricCard
              testId="metric-card-dossiers"
            delay={120}
              label="Dossiers ouverts"
              value={data?.dossiers_open ?? 0}
              hint="Chantiers en cours"
              icon={FolderOpen}
              tone="blue"
              to="/studio/dossiers"
            />
          )}
          {owner ? (
            <MetricCard
              testId="metric-card-ytd"
            delay={180}
              label="Signé cette année"
              value={eur(data?.ytd_accepted)}
              hint={`Ce mois : ${eur(data?.month_accepted)}`}
              icon={TrendingUp}
              tone="emerald"
              to="/studio/chiffre-affaires"
            />
          ) : (
            <MetricCard
              testId="metric-card-interventions"
            delay={180}
              label="Mes interventions"
              value={data?.agenda?.filter((e) => e.type === "intervention").length ?? 0}
              hint="À venir"
              icon={ClipboardCheck}
              tone="blue"
              to="/studio/interventions"
            />
          )}
        </div>
      )}

      {owner && !loading && (
        <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-2">
          <InvoicesWidget inv={inv} />
          <QuotesWidget bd={data?.quotes_breakdown} toInvoice={0} />
        </div>
      )}

      {/* ---------- Mes actions à faire ---------- */}
      {!!data?.todo?.length && (
        <div data-testid="cockpit-todo" className="mt-4 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
          {data.todo.map((t) => (
            <Link
              key={t.key}
              to={t.url}
              data-testid={`todo-${t.key}`}
              className={`flex min-h-[60px] items-center gap-3 rounded-xl border px-4 py-3 transition-colors duration-150 hover:border-[#C9D4F4] ${TONE_RING[t.tone] || TONE_RING.blue}`}
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-bold">{t.label}</span>
                <span className="mt-0.5 block truncate text-xs opacity-80">{t.hint}</span>
              </span>
              <ArrowRight className="h-4 w-4 shrink-0 opacity-60" />
            </Link>
          ))}
        </div>
      )}

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-5">
        {/* ---------- Colonne principale ---------- */}
        <div className="space-y-4 lg:col-span-3">
          <SectionCard
            testId="studio-agenda"
            title="Mes 7 prochains jours"
            hint="Visites techniques et interventions"
            action={
              <Link
                to="/studio/planning"
                data-testid="cockpit-agenda-all"
                className="shrink-0 text-xs font-bold text-[#3A5CC8] hover:underline"
              >
                Planning
              </Link>
            }
          >
            {loading ? (
              <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-14" />)}</div>
            ) : data?.agenda?.length ? (
              <div className="space-y-2">
                {data.agenda.map((e) => (
                  <ListRow
                    key={`${e.type}-${e.id}`}
                    to={e.url}
                    testId={`agenda-${e.reference}`}
                    bar={e.type === "visite" ? "bg-[#3A5CC8]" : "bg-[#3A5CC8]"}
                    title={e.label}
                    subtitle={`${beDate(e.date)}${e.time ? ` à ${e.time}` : ""} · ${e.reference}`}
                    status={e.done ? "signe_valide" : ""}
                  />
                ))}
              </div>
            ) : (
              <EmptyState
                testId="agenda-empty"
                icon={CalendarDays}
                title="Rien de planifié"
                hint="Planifiez une visite technique depuis un dossier chantier, elle apparaîtra ici et le client recevra son rappel la veille."
              />
            )}
          </SectionCard>

          {owner && !!inv?.late_count && (
            <Card
              data-testid="studio-late-invoices"
              className="overflow-hidden border-[#B42318]/20 bg-white"
            >
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#B42318]/15 px-4 py-3.5 sm:px-5">
                <div className="flex min-w-0 items-center gap-2">
                  <AlertTriangle className="h-4 w-4 shrink-0 text-[#B42318]" />
                  <h2 className="truncate font-heading text-base font-bold text-[#B42318]">
                    {inv.late_count} facture{inv.late_count > 1 ? "s" : ""} en retard
                  </h2>
                </div>
                <span className="shrink-0 rounded-full bg-[#B42318] px-2.5 py-0.5 text-xs font-bold tabular-nums text-white">
                  {eur(inv.late_amount)}
                </span>
              </div>
              <div className="space-y-2 px-4 py-3.5 sm:px-5">
                {inv.late_list.map((i) => (
                  <ListRow
                    key={i.id}
                    to={`/studio/factures/${i.id}`}
                    testId={`late-invoice-${i.reference}`}
                    bar="bg-[#B42318]"
                    title={i.reference}
                    subtitle={`${i.client_name} · échue le ${beDate(i.due_date)}`}
                    right={eur(i.balance)}
                  />
                ))}
                <p className="pt-1 text-xs leading-relaxed text-[#B42318]/80">
                  Les rappels partent automatiquement au rythme et au ton choisis dans votre <Link to="/studio/profil" className="font-semibold underline">profil</Link>.
                </p>
              </div>
            </Card>
          )}

          {!!data?.follow_up_list?.length && (
            <SectionCard
              testId="studio-followup"
              title="Devis à relancer"
              hint="Envoyés depuis plus de 3 jours, sans réponse"
            >
              <div className="space-y-2">
                {data.follow_up_list.map((q) => (
                  <ListRow
                    key={q.id}
                    to={`/studio/devis/${q.id}`}
                    testId={`followup-${q.id}`}
                    bar="bg-slate-300"
                    title={q.number}
                    subtitle={q.client_name}
                    right={owner ? eur(q.total) : null}
                  />
                ))}
              </div>
            </SectionCard>
          )}
        </div>

        {/* ---------- Colonne latérale ---------- */}
        <div className="space-y-4 lg:col-span-2">
          <SectionCard testId="cockpit-shortcuts" title="Actions rapides" hint="Les gestes du quotidien">
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: "Nouveau devis", icon: FileText, to: "/studio/devis?nouveau=1", testId: "studio-quick-devis" },
                { label: "Intervention", icon: ClipboardCheck, to: "/studio/interventions?nouveau=1", testId: "studio-quick-interventions" },
                { label: "Parler à Devisto", icon: Mic, to: "/studio/dossiers", testId: "quick-voice" },
                { label: "Bâtiments", icon: Building2, to: "/studio/batiments", testId: "studio-quick-sites" },
                { label: "Entretiens à faire", icon: CalendarClock, to: "/studio/batiments?entretiens=1", testId: "studio-quick-maintenance" },
                { label: "Mon planning", icon: CalendarDays, to: "/studio/planning", testId: "studio-quick-planning" },
                { label: "Mes tarifs", icon: Euro, to: "/studio/bibliotheque", testId: "quick-library" },
              ].map((a) => (
                <Link
                  key={a.testId}
                  to={a.to}
                  data-testid={a.testId}
                  className="flex min-h-[72px] flex-col items-start gap-2 rounded-xl border border-[#E2E7F7] bg-white p-3 transition-all duration-150 hover:-translate-y-0.5 hover:border-[#3A5CC8]/50 active:scale-[0.98]"
                >
                  <a.icon className="h-4.5 w-4.5 text-[#3A5CC8]" />
                  <span className="text-[13px] font-semibold leading-tight text-[#0F2645]">{a.label}</span>
                </Link>
              ))}
            </div>
          </SectionCard>

          <SectionCard testId="cockpit-pipeline" title="Mon pipeline" hint="Du dossier au devis signé">
            <div className="space-y-1.5">
              {[
                { label: "Dossiers ouverts", value: data?.dossiers_open, icon: FolderOpen, to: "/studio/dossiers" },
                { label: "Devis à préparer", value: data?.quotes_to_prepare, icon: Sparkles, to: "/studio/dossiers" },
                { label: "Devis envoyés", value: data?.quotes_sent, icon: FileText, to: "/studio/devis" },
                { label: "Devis signés", value: data?.quotes_accepted, icon: CheckCircle2, to: "/studio/devis" },
                { label: "Devis refusés", value: data?.quotes_refused, icon: Receipt, to: "/studio/devis" },
              ].map((r) => (
                <Link
                  key={r.label}
                  to={r.to}
                  data-testid={`pipeline-${r.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="flex min-h-[44px] items-center gap-3 rounded-xl px-2 transition-colors hover:bg-[#F6F8FD]"
                >
                  <r.icon className="h-4 w-4 shrink-0 text-[#93A0BC]" />
                  <span className="min-w-0 flex-1 truncate text-sm text-[#5C6A87]">{r.label}</span>
                  <span className="shrink-0 font-heading text-base font-extrabold tabular-nums text-[#0F2645]">
                    {data ? (r.value ?? 0) : "—"}
                  </span>
                </Link>
              ))}
            </div>
          </SectionCard>

          {owner && (
            <SectionCard testId="cockpit-money" title="Mon argent" hint="Hors brouillons">
              <div className="space-y-2">
                <Link
                  to="/studio/devis"
                  data-testid="studio-amount-pending"
                  className="flex min-h-[56px] items-center gap-3 rounded-xl border border-[#E2E7F7] bg-[#F6F8FD]/70 px-3 hover:border-[#3A5CC8]/40"
                >
                  <Clock className="h-4 w-4 shrink-0 text-[#93A0BC]" />
                  <span className="min-w-0 flex-1 text-xs font-semibold uppercase tracking-wide text-[#5C6A87]">
                    En attente de réponse
                  </span>
                  <span className="shrink-0 font-heading text-base font-extrabold tabular-nums text-[#0F2645]">
                    {eur(data?.pending_amount)}
                  </span>
                </Link>
                <Link
                  to="/studio/factures"
                  data-testid="studio-amount-open-invoices"
                  className="flex min-h-[56px] items-center gap-3 rounded-xl border border-[#E2E7F7] bg-[#F6F8FD]/70 px-3 hover:border-[#3A5CC8]/40"
                >
                  <Wallet className="h-4 w-4 shrink-0 text-[#3A5CC8]" />
                  <span className="min-w-0 flex-1 text-xs font-semibold uppercase tracking-wide text-[#5C6A87]">
                    Factures à encaisser
                  </span>
                  <span className="shrink-0 font-heading text-base font-extrabold tabular-nums text-[#0F2645]">
                    {eur(inv?.open_amount)}
                  </span>
                </Link>
                <Link
                  to="/studio/chiffre-affaires"
                  data-testid="studio-amount-accepted"
                  className="flex min-h-[56px] items-center gap-3 rounded-xl border border-emerald-100 bg-emerald-50/70 px-3 hover:border-emerald-300"
                >
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
                  <span className="min-w-0 flex-1 text-xs font-semibold uppercase tracking-wide text-emerald-800">
                    Devis signés
                  </span>
                  <span className="shrink-0 font-heading text-base font-extrabold tabular-nums text-emerald-900">
                    {eur(data?.accepted_amount)}
                  </span>
                </Link>
              </div>
            </SectionCard>
          )}
        </div>
      </div>
    </StudioLayout>
  );
}
